import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Chip,
  GlobalStyles
} from '@mui/material';

// Removed Redux auth selectors - now using useAuth() hook
import { useTwilioRoom } from '../../hooks/useTwilioRoom';
import { useVideoTracks } from '../../hooks/useVideoTracks';
import { useParticipants } from '../../hooks/useParticipants';
import { useChat } from '../../hooks/useChat';
import { useDeviceSelection } from '../../hooks/useDeviceSelection';
import { useScreenShare } from '../../hooks/useScreenShare';
import { usePreviewTracks } from '../../hooks/usePreviewTracks';
import { useVideoProcessors } from '../../hooks/useVideoProcessors';
import PreCallPreview from './PreCallPreview';
import VideoGrid from './VideoGrid';
import VideoControls from './VideoControls';
import ChatInterface from './ChatInterface';
import SettingsDialog from './SettingsDialog';
import { useUserData } from '../../hooks/useUserData';

const VideoCallRoom = ({ 
  sessionId,
  patientId, 
  providerId, 
  onCallEnd,
  onQualityReport,
  isAdminTest = false,
  testConfig = {}
}) => {
  const { user } = useUserData();
  // const user = useAuth().user; // Now using useAuth() hook
const navigate = useNavigate();
  const effectiveUserId = isAdminTest && testConfig?.testUserId ? testConfig.testUserId : user?.uid;
  const isProvider = effectiveUserId === providerId;

  // UI State
  const [hasJoinedCall, setHasJoinedCall] = useState(false);
  const [hasHungUp, setHasHungUp] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [videoQuality, setVideoQuality] = useState('720p');
  const [audioQuality, setAudioQuality] = useState('high');
  const [isRecording, setIsRecording] = useState(false);

  // Device Selection Hook
  const deviceSelection = useDeviceSelection();

  // Preview Tracks Hook
  const previewTracks = usePreviewTracks(
    deviceSelection.selectedVideoDeviceId,
    deviceSelection.selectedAudioDeviceId,
    deviceSelection.permissionsGranted
  );

  // Twilio Room Hook
  const twilioRoom = useTwilioRoom(sessionId, effectiveUserId);

  // Video Tracks Hook
  const videoTracks = useVideoTracks(twilioRoom.room);

  // Participants Hook
  const participants = useParticipants(twilioRoom.room);

  // Chat Hook
  const chat = useChat(twilioRoom.room, user);

  // Screen Share Hook
  const screenShare = useScreenShare(twilioRoom.room, deviceSelection.selectedVideoDeviceId);

  // Video Processors Hook
  const videoProcessors = useVideoProcessors();
  const { cleanupProcessor } = videoProcessors;

  // Cleanup video processors on unmount
  useEffect(() => {
    return () => {
      cleanupProcessor();
    };
  }, [cleanupProcessor]); // Only include the specific function we're calling

  // Join the actual video call
  const joinVideoCall = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    // Use existing preview tracks if available, otherwise create new ones
    const tracksToUse = [];
    if (previewTracks.previewTracks.video) {
      tracksToUse.push(previewTracks.previewTracks.video);
    }
    if (previewTracks.previewTracks.audio) {
      tracksToUse.push(previewTracks.previewTracks.audio);
    }

    // Add data track for chat
    if (chat.localDataTrack) {
      tracksToUse.push(chat.localDataTrack);
    }

    const room = await twilioRoom.connectToRoom(tracksToUse);
    if (room) {
      setHasJoinedCall(true);
      
      // Set video tracks state to match the preview state
      videoTracks.setIsVideoEnabled(previewTracks.isVideoEnabled);
      videoTracks.setIsAudioEnabled(previewTracks.isAudioEnabled);
      
      // Wait a bit for tracks to be properly attached before cleaning up preview
      setTimeout(() => {
        // Clean up preview references without stopping the tracks (they're now owned by the room)
        previewTracks.cleanupPreviewTracks(false);
      }, 100);
    }
  }, [sessionId, previewTracks, chat.localDataTrack, twilioRoom, videoTracks]);

  // Handle hang up and navigation
  const handleHangUp = useCallback(() => {
    console.log('Hanging up and navigating to dashboard');
    
    setHasHungUp(true);
    twilioRoom.disconnectFromRoom();
    
    const callDuration = twilioRoom.callDuration();
    
    if (onCallEnd) {
      onCallEnd({ duration: callDuration });
    }
    
    navigate('/dashboard');
  }, [twilioRoom, onCallEnd, navigate]);

  // Handle share link
  const handleShareLink = useCallback(() => {
    const shareLink = `${window.location.origin}/join-telehealth/${sessionId}`;
    navigator.clipboard.writeText(shareLink).then(() => {
      console.log('Link copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy link:', err);
    });
  }, [sessionId]);

  // Handle device changes
  const handleVideoDeviceChange = useCallback((deviceId) => {
    deviceSelection.setSelectedVideoDeviceId(deviceId);
    if (hasJoinedCall) {
      videoTracks.replaceVideoTrack(deviceId);
    } else {
      // During preview, the usePreviewTracks hook will automatically refresh when device changes
      console.log('Video device changed during preview, will auto-refresh');
    }
  }, [deviceSelection, hasJoinedCall, videoTracks]);

  const handleAudioDeviceChange = useCallback((deviceId) => {
    deviceSelection.setSelectedAudioDeviceId(deviceId);
    if (hasJoinedCall) {
      videoTracks.replaceAudioTrack(deviceId);
    } else {
      // During preview, the usePreviewTracks hook will automatically refresh when device changes
      console.log('Audio device changed during preview, will auto-refresh');
    }
  }, [deviceSelection, hasJoinedCall, videoTracks]);

  // Handle background blur toggle
  const handleBackgroundBlurToggle = useCallback(async () => {
    try {
      if (hasJoinedCall) {
        // Get the current local video track
        const localVideoTrack = videoTracks.localVideoTrack;
        if (localVideoTrack) {
          await videoProcessors.toggleBackgroundBlur(localVideoTrack);
        }
      } else {
        // During preview
        const previewVideoTrack = previewTracks.previewTracks.video;
        if (previewVideoTrack) {
          await videoProcessors.toggleBackgroundBlur(previewVideoTrack);
        }
      }
    } catch (error) {
      console.error('Failed to toggle background blur:', error);
    }
  }, [hasJoinedCall, videoTracks.localVideoTrack, previewTracks.previewTracks.video, videoProcessors]);

  // Show pre-call preview if user hasn't joined the call yet
  if (!hasJoinedCall) {
    return (
      <>
        <PreCallPreview
          previewTracks={previewTracks.previewTracks}
          isPreviewLoading={previewTracks.isPreviewLoading}
          previewError={previewTracks.previewError}
          isVideoEnabled={previewTracks.isVideoEnabled}
          isAudioEnabled={previewTracks.isAudioEnabled}
          previewVideoRef={previewTracks.previewVideoRef}
          togglePreviewVideo={previewTracks.togglePreviewVideo}
          togglePreviewAudio={previewTracks.togglePreviewAudio}
          refreshPreview={previewTracks.refreshPreview}
          onJoinCall={joinVideoCall}
          onCancel={() => navigate('/dashboard')}
          onOpenSettings={() => setShowSettings(true)}
          isConnecting={twilioRoom.isConnecting}
          isProvider={isProvider}
          isBackgroundBlurEnabled={videoProcessors.isBackgroundBlurEnabled}
          isProcessorLoading={videoProcessors.isProcessorLoading}
          processorError={videoProcessors.processorError}
          onBackgroundBlurToggle={handleBackgroundBlurToggle}
          permissionsGranted={deviceSelection.permissionsGranted}
          isRequestingPermissions={deviceSelection.isRequestingPermissions}
          deviceError={deviceSelection.deviceError}
          onRequestPermissions={deviceSelection.requestPermissions}
        />
        
        {/* Settings Dialog - Available during preview */}
        <SettingsDialog
          open={showSettings}
          onClose={() => setShowSettings(false)}
          videoInputDevices={deviceSelection.videoInputDevices}
          audioInputDevices={deviceSelection.audioInputDevices}
          selectedVideoDeviceId={deviceSelection.selectedVideoDeviceId}
          selectedAudioDeviceId={deviceSelection.selectedAudioDeviceId}
          onVideoDeviceChange={handleVideoDeviceChange}
          onAudioDeviceChange={handleAudioDeviceChange}
          videoQuality={videoQuality}
          audioQuality={audioQuality}
          onVideoQualityChange={(e) => setVideoQuality(e.target.value)}
          onAudioQualityChange={(e) => setAudioQuality(e.target.value)}
          isRecording={isRecording}
          onRecordingChange={(e) => setIsRecording(e.target.checked)}
          getDeviceLabel={deviceSelection.getDeviceLabel}
          isBackgroundBlurEnabled={videoProcessors.isBackgroundBlurEnabled}
          isProcessorLoading={videoProcessors.isProcessorLoading}
          processorError={videoProcessors.processorError}
          onBackgroundBlurToggle={handleBackgroundBlurToggle}
        />
      </>
    );
  }

  // Show main video call UI after joining
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#1a1a1a',
      color: 'white'
    }}>
      <GlobalStyles styles={{
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 }
        },
        '@keyframes spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' }
        },
        // Ensure all videos display properly without cropping
        'video': {
          objectFit: 'contain !important',
          transform: 'none !important',
          backfaceVisibility: 'hidden !important',
          willChange: 'auto !important'
        },
        // Prevent scaling issues during dialog transitions
        '.MuiDialog-root video': {
          transform: 'none !important',
          backfaceVisibility: 'hidden !important',
          willChange: 'auto !important'
        },
        '.MuiDialog-root *': {
          backfaceVisibility: 'hidden'
        }
      }} />
      {/* Modern Header */}
      <Box sx={{ 
        p: 2, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        position: 'relative',
        zIndex: 10
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Telehealth Session
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: '#4ade80',
              animation: 'pulse 2s infinite'
            }} />
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              This call is encrypted for HIPAA compliance
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            label={twilioRoom.isConnected ? 'SECURE' : twilioRoom.isConnecting ? 'CONNECTING' : 'DISCONNECTED'} 
            sx={{
              backgroundColor: twilioRoom.isConnected ? 'rgba(74, 222, 128, 0.2)' : 
                              twilioRoom.isConnecting ? 'rgba(251, 191, 36, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: twilioRoom.isConnected ? '#4ade80' : 
                     twilioRoom.isConnecting ? '#fbbf24' : '#ef4444',
              border: `1px solid ${twilioRoom.isConnected ? '#4ade80' : 
                                  twilioRoom.isConnecting ? '#fbbf24' : '#ef4444'}`,
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
            size="small"
          />
          {participants.remoteParticipants.length > 0 && (
            <Chip 
              label={`${participants.remoteParticipants.length + 1} participants`}
              sx={{
                backgroundColor: 'rgba(147, 197, 253, 0.2)',
                color: '#93c5fd',
                border: '1px solid #93c5fd',
                fontWeight: 500
              }}
              size="small"
            />
          )}
          <Typography variant="body2" sx={{ 
            color: 'rgba(255,255,255,0.8)',
            fontWeight: 500,
            ml: 1
          }}>
            {isProvider ? 'Provider' : 'Patient'}
          </Typography>
        </Box>
      </Box>
      
      {/* Error Display */}
      {twilioRoom.error && (
        <Box sx={{ 
          mx: 2, 
          mt: 2,
          p: 2, 
          backgroundColor: 'rgba(239, 68, 68, 0.1)', 
          color: '#ef4444',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 2,
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ⚠️ Error: {twilioRoom.error}
          </Typography>
        </Box>
      )}

      {/* Main Content Area - Side by Side Layout */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        backgroundColor: '#1a1a1a',
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Left: Video Area - Fixed size like preview */}
        <Box sx={{ 
          flex: { xs: '1', md: '0 0 auto' },
          width: '100%',
          maxWidth: { xs: '100%', md: 600 },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          {/* Video Container with same aspect ratio as preview */}
          <Box sx={{ 
            aspectRatio: '16/9',
            backgroundColor: '#1e1e1e',
            borderRadius: '20px',
            overflow: 'hidden',
            position: 'relative',
            border: '2px solid rgba(103, 126, 234, 0.3)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
            mb: 2
          }}>
            <VideoGrid 
              participants={participants.allParticipants}
              getParticipantVideoRef={participants.getParticipantVideoRef}
              getParticipantId={participants.getParticipantId}
              room={twilioRoom.room}
            />
          </Box>
          
          {/* Video Controls */}
          <VideoControls
            isVideoEnabled={videoTracks.isVideoEnabled}
            isAudioEnabled={videoTracks.isAudioEnabled}
            isScreenSharing={screenShare.isScreenSharing}
            isConnected={twilioRoom.isConnected}
            hasHungUp={hasHungUp}
            toggleVideo={videoTracks.toggleVideo}
            toggleAudio={videoTracks.toggleAudio}
            toggleScreenShare={screenShare.toggleScreenShare}
            onHangUp={handleHangUp}
            onOpenSettings={() => setShowSettings(true)}
            sessionId={sessionId}
            onShareLink={handleShareLink}
            isBackgroundBlurEnabled={videoProcessors.isBackgroundBlurEnabled}
            isProcessorLoading={videoProcessors.isProcessorLoading}
            processorError={videoProcessors.processorError}
            onBackgroundBlurToggle={handleBackgroundBlurToggle}
          />
        </Box>

        {/* Right: Chat Interface - Takes remaining space */}
        <Box sx={{ 
          flex: 1,
          minWidth: { xs: 'auto', md: 300 },
          minHeight: { xs: 300, md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          <ChatInterface
            chatMessages={chat.chatMessages}
            chatInput={chat.chatInput}
            setChatInput={chat.setChatInput}
            sendChatMessage={chat.sendChatMessage}
            handleChatKeyPress={chat.handleChatKeyPress}
            isConnected={twilioRoom.isConnected}
            hasHungUp={hasHungUp}
          />
        </Box>
      </Box>

      {/* Settings Dialog */}
      <SettingsDialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        videoInputDevices={deviceSelection.videoInputDevices}
        audioInputDevices={deviceSelection.audioInputDevices}
        selectedVideoDeviceId={deviceSelection.selectedVideoDeviceId}
        selectedAudioDeviceId={deviceSelection.selectedAudioDeviceId}
        onVideoDeviceChange={handleVideoDeviceChange}
        onAudioDeviceChange={handleAudioDeviceChange}
        videoQuality={videoQuality}
        audioQuality={audioQuality}
        onVideoQualityChange={(e) => setVideoQuality(e.target.value)}
        onAudioQualityChange={(e) => setAudioQuality(e.target.value)}
        isRecording={isRecording}
        onRecordingChange={(e) => setIsRecording(e.target.checked)}
        getDeviceLabel={deviceSelection.getDeviceLabel}
        isBackgroundBlurEnabled={videoProcessors.isBackgroundBlurEnabled}
        isProcessorLoading={videoProcessors.isProcessorLoading}
        processorError={videoProcessors.processorError}
        onBackgroundBlurToggle={handleBackgroundBlurToggle}
      />

      {/* Reconnecting Indicator */}
      {twilioRoom.isReconnecting && (
        <Box sx={{ 
          mx: 2,
          mb: 2,
          p: 2, 
          backgroundColor: 'rgba(251, 191, 36, 0.1)', 
          color: '#fbbf24',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{ 
            width: 16, 
            height: 16, 
            border: '2px solid #fbbf24',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <Typography sx={{ fontWeight: 500 }}>
            Reconnecting to the session…
          </Typography>
        </Box>
      )}
      
      {/* Hang Up Message */}
      {hasHungUp && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <Box sx={{ 
            p: 4, 
            backgroundColor: 'white', 
            borderRadius: 2, 
            textAlign: 'center',
            maxWidth: 400
          }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Call Ended
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Redirecting to dashboard...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VideoCallRoom; 